import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CarCard from "@/components/CarCard";
import { Button } from "@/components/ui/button";
import { Search, MapPin, ShieldCheck, CalendarDays, KeyRound } from "lucide-react";
import HeroStepperSearch from "@/components/HeroStepperSearch";
import HeroMobileSearch from "@/components/HeroMobileSearch";
import HeroSlider from "@/components/home/HeroSlider";
import StickySearchBar from "@/components/home/StickySearchBar";
import VendorLogoCarousel from "@/components/home/VendorLogoCarousel";
import RecentVendorsStrip from "@/components/RecentVendorsStrip";
import TopDestinationsChipRow from "@/components/home/TopDestinationsChipRow";
import WhyRentalCar from "@/components/home/WhyRentalCar";
import TopPartnersMiniCard from "@/components/home/TopPartnersMiniCard";
import GoogleReviewsShowcase from "@/components/home/GoogleReviewsShowcase";
import PopularDestinationsSection from "@/components/home/PopularDestinationsSection";
import PopularSearchesSection from "@/components/home/PopularSearchesSection";
import TravelTipsSection from "@/components/home/TravelTipsSection";
import HomeFaqPreview from "@/components/home/HomeFaqPreview";
import HeroTrustPills from "@/components/home/HeroTrustPills";
import TopLocationDestinations from "@/components/home/TopLocationDestinations";
import FeaturedListings from "@/components/home/FeaturedListings";

export default function Home() {
  usePageMeta("meta.home.title", "meta.home.description");
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);
  // Iter 67aa: lift the hero stepper's country so the vendor-logo carousel under
  // the search engine can filter to local hosts in the chosen country.
  const [selectedCountry, setSelectedCountry] = useState("");
  // Iter 80 — sticky compact search bar appears when the hero scrolls away.
  const heroRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div>
      <Header />
      <StickySearchBar heroRef={heroRef} />

      {/* Hero — iter 67ea.
          • Desktop: title + subtitle overlaid on top of the country-aware
            HeroSlider so the photo carries the brand mood. A dark
            gradient ensures contrast over bright Albanian-coast frames.
          • Mobile/tablet: slider hidden (per iter 67dr) — title sits
            inline, then the vertical mobile search card. */}
      <section ref={heroRef} className="relative hero-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-12 pb-10 lg:pb-40 relative">
          {/* Mobile / tablet: title above (no overlay since the image is hidden). */}
          <div className="lg:hidden max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-semibold leading-[0.95]">
              {t("home.hero.title_line1", "Car rentals")}<br />
              <span className="text-red-600">{t("home.hero.title_line2", "in Albania")}</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              {t("home.hero.subtitle",
                "Compare real offers from trusted local hosts at Tirana International Airport Nënë Tereza (TIA), Saranda, Vlora, Durrës, Ksamil, Gjirokastër, and more across Albania.")}
            </p>
            <HeroTrustPills variant="light" />
          </div>

          {/* Desktop: hero image w/ overlaid title. */}
          <div className="hidden lg:block relative">
            <HeroSlider country={selectedCountry} />
            {/* Dark gradient + grain for legibility on bright photos. */}
            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div
              className="absolute top-10 left-10 xl:top-14 xl:left-14 max-w-2xl"
              data-testid="hero-overlay-text"
            >
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-semibold leading-[0.95] text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)]">
                {t("home.hero.title_line1", "Car rentals")}<br />
                <span className="text-amber-300">{t("home.hero.title_line2", "in Albania")}</span>
              </h1>
              {/* Iter 68 — line-clamp the subtitle to 3 lines on desktop so
                  longer translations (FR, DE, IT, AL...) don't push the
                  trust pills down into the search stepper card territory
                  and visually disappear behind it. The full subtitle is
                  still readable on mobile (no clamp there). */}
              <p className="mt-5 text-lg xl:text-xl text-white/95 max-w-xl leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)] line-clamp-3">
                {t("home.hero.subtitle",
                  "Compare real offers from trusted local hosts at Tirana International Airport Nënë Tereza (TIA), Saranda, Vlora, Durrës, Ksamil, Gjirokastër, and more across Albania.")}
              </p>
              <HeroTrustPills variant="dark" />
            </div>
          </div>

          {/* Iter 66: stepped search (desktop only). Iter 67cw: dedicated
              mobile/tablet vertical search engine on `< lg`. */}
          <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 -bottom-12 w-[92%] max-w-5xl">
            <HeroStepperSearch onCountryChange={setSelectedCountry} />
          </div>
          <div className="lg:hidden mt-8 -mx-1">
            <HeroMobileSearch />
          </div>
        </div>
      </section>

      {/* Iter 67dx / 67ea — Top-10 destinations chip row, sits right under
          the hero so renters discover SEO landing pages without scrolling.
          Extra `lg:mt-40` clears the absolute-positioned search stepper
          card that overhangs the hero on desktop (search-card height +
          `-bottom-12`). */}
      <div className="lg:mt-40">
        <TopDestinationsChipRow />
      </div>

      {/* Iter 70 → 74p — Featured Listings teaser. User asked to place it
          directly under Top Destinations (right below the search engine),
          so it sits above RecentVendorsStrip now. Carousel on desktop,
          vertical stack on mobile (see FeaturedListings.jsx layout logic). */}
      <FeaturedListings
        variant="teaser"
        limit={20}
        kicker="Featured"
        headline="Handpicked cars this month"
      />

      {/* Iter 67ad — "Recently viewed hosts" strip; self-hides until the
          renter has clicked at least one logo or visited a car. */}
      <RecentVendorsStrip className="pt-14 -mb-4" />

      {/* 1. Partner Hosts Carousel — filters by selected country. */}
      <VendorLogoCarousel country={selectedCountry} />

      {/* 2. Why Book with RentalCar.al? — value props after social proof. */}
      <WhyRentalCar />

      {/* 2a. Iter 67ek — Top 3 partner sites embedding our search widget.
              Self-hides when no public profiles have confirmed bookings in
              the trailing 90 days. */}
      <TopPartnersMiniCard />

      {/* 3. Popular Destinations — phase-1 placeholder; CMS-powered in phase 3. */}
      <PopularDestinationsSection />

      {/* 3a. Iter 67cl — Trending searches strip (live data from the last 30 days). */}
      <PopularSearchesSection />

      {/* Iter 70 — Featured Listings full grid (mid-page, 8 cars). Sits just
          above the "Trending now" section so featured picks get pride of
          place without pushing organic trending too far down the page. */}
      <FeaturedListings variant="grid" limit={8} />

      {/* 4. Trending Now / Popular Near You — featured cars. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3">Trending now</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">Popular near you</h2>
          </div>
          <Link to="/cars" className="text-sm font-medium text-blue-600 hover:underline" data-testid="see-all-cars-link">See all →</Link>
        </div>
        {cars.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center">
            <div className="text-neutral-700 font-medium mb-1">No cars listed yet</div>
            <div className="text-sm text-neutral-500 mb-4">Be the first vendor to list a car on RentalCar.al.</div>
            <Link to="/become-vendor"><Button className="rounded-full bg-blue-600 hover:bg-blue-700">Become a host</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
            {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        )}
      </section>

      {/* 5. Trusted by Travelers — Google reviews showcase, now with country chips. */}
      <GoogleReviewsShowcase />

      {/* 6. Travel Tips for Renters — phase-1 placeholder; CMS-powered in phase 3. */}
      <TravelTipsSection />

      {/* 7. FAQ — Iter 67en, popularity-driven Top-6 accordion fed by the
          chat-analytics promote pipeline. Emits FAQPage JSON-LD for
          Google rich results. Self-hides when no FAQs are published. */}
      <HomeFaqPreview />

      {/* 8. Iter 67cy — Top car-rental destinations link grid, fed by the
          locations catalog. Each pill deep-links into a SEO landing page
          at /car-rental/:slug for crawler discovery + renter navigation. */}
      <TopLocationDestinations />

      <Footer />
    </div>
  );
}
